import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getFamilyHistoryIntake } from './familyHistoryIntakeService';
import type { PatientFamilyHistoryIntakeRecord } from '../types/familyHistoryIntake';
import type {
  MemberCareTeamAssignment,
  MemberCareTeamMember,
  MemberCareTeamView,
} from '../types/memberCareTeam';

function normalizeMember(raw: unknown, role: MemberCareTeamMember['role']): MemberCareTeamMember | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const name = typeof data.name === 'string' ? data.name.trim() : '';
  if (!name) return null;
  return {
    name,
    role,
    title: typeof data.title === 'string' ? data.title : null,
    photoUrl: typeof data.photoUrl === 'string' ? data.photoUrl : null,
    bio: typeof data.bio === 'string' ? data.bio : null,
  };
}

/**
 * The care team is two people: the Relationship Manager and the Senior Care
 * Clinician. A legacy `careClinician` block may still sit on the member doc
 * until the field drop runs — it is deliberately not read, so the card shows
 * the two live slots rather than a person who no longer has that role.
 */
function fromAssignment(assignment: MemberCareTeamAssignment | null | undefined): MemberCareTeamMember[] {
  if (!assignment) return [];
  const members: MemberCareTeamMember[] = [];
  const coordinator = normalizeMember(assignment.coordinator, 'coordinator');
  const senior = normalizeMember(assignment.senior, 'medical_director');
  if (coordinator) members.push(coordinator);
  if (senior) members.push(senior);
  return members;
}

function roleTitle(role: MemberCareTeamMember['role']): string {
  switch (role) {
    case 'coordinator':
      return 'Relationship Manager';
    case 'medical_director':
      return 'Senior Care Clinician';
    default:
      return 'Clinician';
  }
}

export interface GetMemberCareTeamOptions {
  email?: string | null;
  displayName?: string | null;
  /**
   * Already-fetched documents. The dashboard loads the user doc and the intake
   * for other blocks anyway, so passing them here saves one or two round trips
   * on the critical path. Omit both and this reads them itself, as before.
   */
  userData?: Record<string, unknown> | null;
  intake?: PatientFamilyHistoryIntakeRecord | null;
}

export async function getMemberCareTeam(
  userId: string,
  options?: GetMemberCareTeamOptions,
): Promise<MemberCareTeamView> {
  let userData = options?.userData ?? null;
  if (!userData) {
    const userSnap = await getDoc(doc(db, 'users', userId));
    userData = userSnap.exists() ? (userSnap.data() as Record<string, unknown>) : {};
  }
  const assignment = userData.careTeam as MemberCareTeamAssignment | undefined;
  let members = fromAssignment(assignment);

  if (members.length === 0) {
    const intake = options?.intake !== undefined
      ? options.intake
      : await getFamilyHistoryIntake(userId, options).catch(() => null);
    const fallback: MemberCareTeamMember[] = [];
    // The intake carries the senior's name under the new key; members whose
    // intake predates the care-clinician removal only have the old one, and
    // that person is now their senior.
    const seniorName = intake?.assignedSeniorClinicianName
      || intake?.assignedCareClinicianName
      || intake?.assignedMedicalDirectorName;
    if (seniorName) {
      fallback.push({
        name: seniorName,
        role: 'medical_director',
        title: roleTitle('medical_director'),
      });
    }
    members = fallback;
  }

  return {
    members: members.map((member) => ({
      ...member,
      title: member.title || roleTitle(member.role),
    })),
    isEmpty: members.length === 0,
  };
}
